import React from 'react';
import {
  CATEGORY6CONSTANTS,
  travelModeOptions,
  spendTypeOptions,
  carSizeOptions,
  carTypeOptions,
  flightClassOptions,
  currencyOptions,
} from 'constants/Scope3Constants/category6.constants';
import { FieldType } from 'enums/FieldTypeEnums';
import { FieldsRowProps } from 'shared/components/FieldsRow';
import { allowOnlyNumbers, allowOnlyNumbersWithoutDots } from 'shared/utils/convertFormData';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import { ReactComponent as MapSearch } from 'assets/icons/map-search.svg';
import styles from './category6.module.scss';
import { UseFieldArrayRemove, useFormContext, Controller } from 'react-hook-form';
import { Switch } from 'antd';
import { BusinessTravelData } from 'models/ActivityDataEntryScopes/businessTravel.model';

const { INPUT, SELECT, ELEMENT } = FieldType;
const {
  LABELS: {
    TRAVEL_MODE,
    ORIGIN,
    DESTINATION,
    ROUND_TRIP,
    NUMBER_OF_PASSENGERS,
    CAR_SIZE,
    CAR_TYPE,
    FLIGHT_CLASS,
    SPEND_TYPE,
    MONEY,
    MONEY_UNIT,
    SPEND_LOCATION,
    HOTEL_NIGHTS,
    LOCATION,
  },
  FIELD_TRAVEL_MODE,
  FIELD_ORIGIN,
  FIELD_DESTINATION,
  FIELD_NUMBER_OF_PASSENGERS,
  FIELD_CAR_SIZE,
  FIELD_CAR_TYPE,
  FIELD_FLIGHT_CLASS,
  FIELD_SPEND_TYPE,
  FIELD_MONEY,
  FIELD_MONEY_UNIT,
  FIELD_SPEND_LOCATION,
  FIELD_HOTEL_NIGHTS,
  FIELD_LOCATION,
  rowGutter,
  formType,
} = CATEGORY6CONSTANTS;

interface Category6LayoutDataProps {
  index?: number;
  remove?: UseFieldArrayRemove;
  arrayLength?: number;
  handleChange?: () => void;
  travelMode?: string | null;
  toggleOriginModal?: (index: number) => void;
  toggleDestinationModal?: (index: number) => void;
  toggleSpendLocationModal?: (index: number) => void;
  toggleHotelLocationModal?: (index: number) => void;
  readOnly?: boolean;
}

const commonProps = {
  isNullable: true,
  info: false,
};

const commonRowProps = {
  rowGutter,
  formType,
};

// Custom Round Trip Switch Component with label below
const RoundTripSwitch: React.FC<{
  index: number;
  handleChange?: () => void;
  disabled?: boolean;
}> = ({ index, handleChange, disabled }) => {
  const { control } = useFormContext<BusinessTravelData>();
  const fieldName = `distanceBasedTravel.${index}.roundTrip` as const;

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field }) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <Switch
            checked={Boolean(field.value ?? false)}
            disabled={disabled}
            onChange={checked => {
              field.onChange(checked);
              handleChange?.();
            }}
          />
          <span style={{ fontSize: '12px', color: '#666', fontWeight: 300 }}>{ROUND_TRIP}</span>
        </div>
      )}
    />
  );
};

// Distance-based travel fields
export const getDistanceBasedFieldsData = (
  props: Category6LayoutDataProps
): FieldsRowProps => {
  const {
    index,
    arrayLength,
    remove,
    handleChange,
    travelMode,
    toggleOriginModal,
    toggleDestinationModal,
    readOnly,
  } = props;

  const ro = !!readOnly;
  const removeEntry = () => remove?.(index);

  const isCarTravel = travelMode === 'car';
  const isAirTravel = travelMode === 'air';

  const handleOpenOriginModal = () => toggleOriginModal?.(index ?? 0);
  const handleOpenDestinationModal = () => toggleDestinationModal?.(index ?? 0);

  return {
    data: [
      {
        type: SELECT,
        fieldProps: {
          name: `distanceBasedTravel.${index}.${FIELD_TRAVEL_MODE}`,
          placeholderstring: TRAVEL_MODE,
          label: TRAVEL_MODE,
          options: travelModeOptions,
          onChange: handleChange,
          disabled: ro,
          ...commonProps,
        },
        span: 3,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `distanceBasedTravel.${index}.${FIELD_ORIGIN}`,
          placeholderstring: ORIGIN,
          label: ORIGIN,
          onClick: ro ? undefined : handleOpenOriginModal,
          suffix: <MapSearch />,
          readOnly: true,
          disabled: ro,
          ...commonProps,
        },
        span: 4,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `distanceBasedTravel.${index}.${FIELD_DESTINATION}`,
          placeholderstring: DESTINATION,
          label: DESTINATION,
          onClick: ro ? undefined : handleOpenDestinationModal,
          suffix: <MapSearch />,
          readOnly: true,
          disabled: ro,
          ...commonProps,
        },
        span: 4,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `distanceBasedTravel.${index}.${FIELD_NUMBER_OF_PASSENGERS}`,
          placeholderstring: NUMBER_OF_PASSENGERS,
          label: NUMBER_OF_PASSENGERS,
          onKeyDown: allowOnlyNumbersWithoutDots,
          onChange: handleChange,
          disabled: ro,
          ...commonProps,
        },
        span: 3,
      },
      ...(isCarTravel
        ? [
          {
            type: SELECT,
            fieldProps: {
              name: `distanceBasedTravel.${index}.${FIELD_CAR_SIZE}`,
              placeholderstring: CAR_SIZE,
              label: CAR_SIZE,
              options: carSizeOptions,
              onChange: handleChange,
              disabled: ro,
              ...commonProps,
            },
            span: 2.5,
          },
          {
            type: SELECT,
            fieldProps: {
              name: `distanceBasedTravel.${index}.${FIELD_CAR_TYPE}`,
              placeholderstring: CAR_TYPE,
              label: CAR_TYPE,
              options: carTypeOptions,
              onChange: handleChange,
              disabled: ro,
              ...commonProps,
            },
            span: 2.5,
          },
        ]
        : []),
      ...(isAirTravel
        ? [
          {
            type: SELECT,
            fieldProps: {
              name: `distanceBasedTravel.${index}.${FIELD_FLIGHT_CLASS}`,
              placeholderstring: FLIGHT_CLASS,
              label: FLIGHT_CLASS,
              options: flightClassOptions,
              onChange: handleChange,
              disabled: ro,
              ...commonProps,
            },
            span: 2.5,
          },
        ]
        : []),
      {
        type: ELEMENT,
        additionalElement: (
          <RoundTripSwitch
            index={index ?? 0}
            handleChange={handleChange}
            disabled={ro}
          />
        ),
        className: styles.alignCenter,
        span: 2,
      },
      {
        type: ELEMENT,
        ...(arrayLength && arrayLength >= 1 && !readOnly
          ? {
            additionalElement: (
              <div className={styles.closeIcon}>
                <DeleteButton onClick={removeEntry} />
              </div>
            ),
          }
          : {}),
        className: styles.alignCenter,
      },
    ],
    rowClassName: styles.wrapClassname,
    ...commonRowProps,
  };
};

// Spend-based travel fields
export const getSpendBasedFieldsData = (
  props: Category6LayoutDataProps
): FieldsRowProps => {
  const {
    index,
    arrayLength,
    remove,
    handleChange,
    toggleSpendLocationModal,
    readOnly,
  } = props;

  const ro = !!readOnly;
  const removeEntry = () => remove?.(index);

  const handleOpenSpendLocationModal = () => toggleSpendLocationModal?.(index ?? 0);

  return {
    data: [
      {
        type: SELECT,
        fieldProps: {
          name: `spendBasedTravel.${index}.${FIELD_SPEND_TYPE}`,
          placeholderstring: SPEND_TYPE,
          label: SPEND_TYPE,
          options: spendTypeOptions,
          onChange: handleChange,
          disabled: ro,
          ...commonProps,
        },
        span: 5,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `spendBasedTravel.${index}.${FIELD_MONEY}`,
          placeholderstring: MONEY,
          label: MONEY,
          onKeyDown: allowOnlyNumbers,
          onChange: handleChange,
          disabled: ro,
          ...commonProps,
        },
        span: 5,
      },
      {
        type: SELECT,
        fieldProps: {
          name: `spendBasedTravel.${index}.${FIELD_MONEY_UNIT}`,
          placeholderstring: MONEY_UNIT,
          label: MONEY_UNIT,
          options: currencyOptions,
          onChange: handleChange,
          disabled: ro,
          ...commonProps,
        },
        span: 4,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `spendBasedTravel.${index}.${FIELD_SPEND_LOCATION}`,
          placeholderstring: SPEND_LOCATION,
          label: SPEND_LOCATION,
          onClick: ro ? undefined : handleOpenSpendLocationModal,
          suffix: <MapSearch />,
          readOnly: true,
          disabled: ro,
          ...commonProps,
        },
        span: 5,
      },
      {
        type: ELEMENT,
        ...(arrayLength && arrayLength >= 1 && !readOnly
          ? {
            additionalElement: (
              <div className={styles.closeIcon}>
                <DeleteButton onClick={removeEntry} />
              </div>
            ),
          }
          : {}),
        className: styles.alignCenter,
      },
    ],
    rowClassName: styles.wrapClassname,
    ...commonRowProps,
  };
};

// Hotel stay fields
export const getHotelStayFieldsData = (
  props: Category6LayoutDataProps
): FieldsRowProps => {
  const {
    index,
    arrayLength,
    remove,
    handleChange,
    toggleHotelLocationModal,
    readOnly,
  } = props;

  const ro = !!readOnly;
  const removeEntry = () => remove?.(index);

  const handleOpenHotelLocationModal = () => toggleHotelLocationModal?.(index ?? 0);

  return {
    data: [
      {
        type: INPUT,
        fieldProps: {
          name: `hotelStays.${index}.${FIELD_LOCATION}`,
          placeholderstring: LOCATION,
          label: LOCATION,
          onClick: ro ? undefined : handleOpenHotelLocationModal,
          suffix: <MapSearch />,
          readOnly: true,
          disabled: ro,
          ...commonProps,
        },
        span: 8,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `hotelStays.${index}.${FIELD_HOTEL_NIGHTS}`,
          placeholderstring: HOTEL_NIGHTS,
          label: HOTEL_NIGHTS,
          onKeyDown: allowOnlyNumbers,
          onChange: handleChange,
          disabled: ro,
          ...commonProps,
        },
        span: 6,
      },
      {
        type: ELEMENT,
        ...(arrayLength && arrayLength >= 1 && !readOnly
          ? {
            additionalElement: (
              <div className={styles.closeIcon}>
                <DeleteButton onClick={removeEntry} />
              </div>
            ),
          }
          : {}),
        className: styles.alignCenter,
      },
    ],
    rowClassName: styles.wrapClassname,
    ...commonRowProps,
  };
};
